import React from 'react'
import { Toast as HotToast, resolveValue, toast as hotToast } from 'react-hot-toast'
import { Box, styled } from '@mui/material'
import { Close } from '@mui/icons-material'

/**
 * The one toast card, used app-wide — dark "night" chrome regardless of the
 * page underneath.
 *
 * Renders inside <Toaster>'s `children` render-prop in `App.tsx`, replacing
 * `react-hot-toast`'s default white/library styling — every existing
 * `toast.success(...)` / `toast.error(...)` / `toast(...)` call site picks
 * this up with zero code changes.
 */

export type ToastVisualVariant = 'success' | 'error' | 'warning' | 'info' | 'loading'

/**
 * `react-hot-toast` only has success/error/loading/blank/custom types — it
 * has no first-class "warning" concept. Call sites that want the warning
 * treatment on a plain `toast(...)` opt in with this sentinel icon:
 *
 *   toast('Deleting is permanent — this cannot be undone.', { icon: WARNING_ICON })
 */
export const WARNING_ICON = '__ds_warning__'

function resolveVariant(t: HotToast): ToastVisualVariant {
  if (t.type === 'success') return 'success'
  if (t.type === 'error') return 'error'
  if (t.type === 'loading') return 'loading'
  if (t.icon === WARNING_ICON) return 'warning'
  return 'info'
}

/** A short custom icon glyph (emoji/short string) passed via `{ icon: '➤' }`, if any. */
function resolveCustomGlyph(t: HotToast): string | null {
  if (typeof t.icon === 'string' && t.icon !== WARNING_ICON && t.icon.length <= 2) return t.icon
  return null
}

const GLYPH: Record<ToastVisualVariant, string> = {
  success: '✓',
  error: '✕',
  warning: '!',
  info: 'i',
  loading: '',
}

const Card = styled(Box)({
  position: 'relative',
  width: 360,
  maxWidth: 'calc(100vw - 32px)',
  borderRadius: 'var(--ds-radius-md)',
  background: 'var(--ds-night-canvas, #0e0d12)',
  boxShadow: 'var(--ds-shadow-night, 0 30px 80px rgba(0,0,0,0.5))',
  padding: '13px 14px',
  display: 'flex',
  gap: 11,
  alignItems: 'flex-start',
  fontFamily: 'var(--ds-font)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: 2,
    background: 'var(--ds-gradient-spectrum, linear-gradient(100deg, #ffcf7d, #fea67d, #be9dfd, #01dafe))',
  },
})

const IconChip = styled('span', { shouldForwardProp: (p) => p !== 'variant' })<{
  variant: ToastVisualVariant
}>(({ variant }) => ({
  width: 26,
  height: 26,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  fontWeight: 700,
  flexShrink: 0,
  marginTop: 1,
  color: '#fff',
  // Spectrum hues are light — pair every chip with its dark ink for contrast.
  ...(variant === 'success' && {
    background: 'var(--ds-alert-ok, #01dafe)',
    color: 'var(--ds-alert-ok-ink, #0a6b7a)',
  }),
  ...(variant === 'error' && {
    background: 'var(--ds-alert-err, #fea67d)',
    color: 'var(--ds-alert-err-ink, #9a4a32)',
  }),
  ...(variant === 'warning' && {
    background: 'var(--ds-alert-warn, #ffcf7d)',
    color: 'var(--ds-alert-warn-ink, #8a5a00)',
  }),
  ...(variant === 'info' && {
    background: 'var(--ds-alert-info, #be9dfd)',
    color: 'var(--ds-alert-info-ink, #5a3a8f)',
  }),
  ...(variant === 'loading' && {
    background: 'transparent',
    boxShadow: 'inset 0 0 0 2px var(--ds-night-border, rgba(244,243,246,0.16))',
    borderTopColor: 'var(--ds-night-ink, #f4f3f6)',
    animation: 'ds-toast-spin 800ms linear infinite',
  }),
}))

const Title = styled('div')({
  fontSize: 13.5,
  fontWeight: 640,
  color: 'var(--ds-night-ink, #f4f3f6)',
  letterSpacing: '-0.01em',
  lineHeight: 1.4,
})

const CloseBtn = styled('button')({
  width: 20,
  height: 20,
  borderRadius: 6,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  border: 'none',
  background: 'transparent',
  color: 'var(--ds-night-ink-subtle, rgba(244,243,246,0.36))',
  cursor: 'pointer',
  transition: 'all var(--ds-duration-fast) var(--ds-ease)',
  '&:hover': {
    color: 'var(--ds-night-ink, #f4f3f6)',
    background: 'rgba(244,243,246,0.1)',
  },
})

export interface ToastCardProps {
  toast: HotToast
}

/** The visual card rendered for every toast — pass as `<Toaster>{(t) => <ToastCard toast={t} />}</Toaster>`. */
export function ToastCard({ toast: t }: ToastCardProps) {
  const variant = resolveVariant(t)
  const glyph = resolveCustomGlyph(t) ?? GLYPH[variant]

  return (
    <Card
      role={t.ariaProps.role}
      aria-live={t.ariaProps['aria-live']}
      sx={{
        opacity: t.visible ? 1 : 0,
        transform: t.visible ? 'translateY(0) scale(1)' : 'translateY(-8px) scale(0.97)',
        transition: 'all 220ms var(--ds-ease)',
      }}
    >
      <IconChip variant={variant}>{glyph}</IconChip>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Title>{resolveValue(t.message, t)}</Title>
      </Box>
      {t.type !== 'loading' && (
        <CloseBtn aria-label='Dismiss' onClick={() => hotToast.dismiss(t.id)}>
          <Close sx={{ fontSize: 13 }} />
        </CloseBtn>
      )}
    </Card>
  )
}
